/**
 * Client side of the wishlist ("Merkzettel").
 *
 * Storage model: localStorage holds the working copy on every device, as
 * `{ items, removed, owner }` with items newest-first. For logged-in customers
 * the customer metafield custom.wishlist (list.product_reference) is the
 * central storage; pages inject its state as FoxTheme.wishlist.metafieldItems
 * and the effective list is local items first (they are newer), then metafield
 * items, minus handles removed on this device (`removed` tombstones the
 * metafield entries until a backend can delete them for real). `owner` scopes
 * the tombstones to one account on shared devices.
 *
 * The theme cannot write customer metafields itself — that goes through the
 * wishlist app's proxy endpoint (settings.wishlist_proxy_url). Mutations by
 * logged-in customers queue as delta operations in localStorage and flush
 * sequentially via persist()/flush(); with no endpoint configured everything
 * stays device-local.
 *
 * Guarded against re-execution: theme.liquid loads this file globally when the
 * wishlist is enabled, and the wishlist page section loads it again so the page
 * works independent of DOM order of the deferred scripts.
 */
if (!window.WishlistUtils) {
  class WishlistUtils {
    static storageKey = 'hypertheme:wishlist';

    static getState() {
      let state = null;
      try {
        state = FoxTheme.utils.getStorage(this.storageKey);
      } catch (error) {
        // A corrupted value must not kill every wishlist code path — start fresh.
        console.error(error);
      }
      return {
        items: Array.isArray(state?.items) ? state.items : [],
        removed: Array.isArray(state?.removed) ? state.removed : [],
        pending: Array.isArray(state?.pending) ? state.pending : [],
        owner: state?.owner || null,
      };
    }

    static setState(state) {
      if (!FoxTheme.config.hasLocalStorage) return;
      if (state.items.length > 0 || state.removed.length > 0 || state.pending.length > 0) {
        FoxTheme.utils.setStorage(this.storageKey, state);
      } else {
        localStorage.removeItem(this.storageKey);
      }
    }

    /**
     * Tombstones must not leak between accounts on a shared device: another
     * customer's `removed` list would silently hide this customer's metafield
     * items. Guest items are kept — they were added on this device and merge
     * into whoever logs in.
     */
    static adoptOwner() {
      const customerId = FoxTheme.wishlist?.customerId || null;
      if (!customerId) return;
      const state = this.getState();
      if (state.owner === customerId) return;
      const guestItems = state.owner ? [] : state.items;
      this.setState({
        items: guestItems,
        removed: [],
        pending: [],
        owner: customerId,
      });
      // First login on this device: push the guest list into central storage
      // (the predecessor app's /login merge). With no endpoint configured the
      // items simply stay local.
      if (guestItems.length > 0) {
        this.persist({ action: 'merge', products: guestItems });
      }
    }

    static getMetafieldItems() {
      const items = FoxTheme.wishlist?.metafieldItems || [];
      // Dangling product references resolve to nil in Liquid and arrive as nulls.
      return items.filter((item) => item.handle);
    }

    /**
     * Effective wishlist, newest first.
     * @returns {Array<{handle: string, id: number}>}
     */
    static getItems() {
      const state = this.getState();
      const seen = new Set(state.items.map((item) => item.handle));
      const merged = [...state.items];

      this.getMetafieldItems().forEach((item) => {
        if (!seen.has(item.handle) && !state.removed.includes(item.handle)) {
          merged.push(item);
          seen.add(item.handle);
        }
      });

      return merged;
    }

    static has(handle) {
      return this.getItems().some((item) => item.handle === handle);
    }

    static add(handle, id) {
      const state = this.getState();
      state.removed = state.removed.filter((removedHandle) => removedHandle !== handle);
      if (!state.items.some((item) => item.handle === handle)) {
        state.items.unshift({ handle, id });
      }
      this.setState(state);
      this.persist({ action: 'add', handle, id });
      this.updateButtons();
      FoxTheme.pubsub.publish('wishlist:add', { handle, id, items: this.getItems() });
    }

    static remove(handle) {
      const knownItem = this.getItems().find((item) => item.handle === handle);
      const state = this.getState();
      state.items = state.items.filter((item) => item.handle !== handle);
      if (this.getMetafieldItems().some((item) => item.handle === handle) && !state.removed.includes(handle)) {
        state.removed.push(handle);
      }
      this.setState(state);
      this.persist({ action: 'remove', handle, id: knownItem ? knownItem.id : null });
      this.updateButtons();
      FoxTheme.pubsub.publish('wishlist:remove', { handle, items: this.getItems() });
    }

    static toggle(handle, id) {
      if (this.has(handle)) {
        this.remove(handle);
      } else {
        this.add(handle, id);
      }
      return this.has(handle);
    }

    /**
     * Sync layer for the customer metafield (custom.wishlist) via the wishlist
     * app's proxy endpoint. Delta operations (not full-list replace) mirror the
     * mobile app's calls against the old Magento wishlist API and cannot
     * clobber concurrent edits from other devices. The queue lives in
     * localStorage, so a failed request or a navigation mid-sync is retried on
     * the next mutation or page load.
     *
     * Route shape verified against the predecessor app (abrio-shopify-team/
     * abrio-wishlist, theme extension abrwl-core.js): POST/DELETE
     * {proxy}/product/{id} to add/remove (idempotent server-side), POST
     * {proxy}/login with {products} to merge a guest list on login. Open for
     * the Hagel adaptation (sync 2026-08-25): whether the {listId} body field
     * survives the switch to the single-list product_reference metafield —
     * adjust buildSyncRequest() only.
     */
    static persist(operation) {
      if (!operation || !FoxTheme.wishlist?.customerId || !FoxTheme.wishlist?.proxyUrl) return;
      const state = this.getState();
      state.pending.push(operation);
      this.setState(state);
      this.flush();
    }

    static buildSyncRequest(operation) {
      const base = FoxTheme.wishlist.proxyUrl.replace(/\/$/, '');
      const config = {
        method: operation.action === 'remove' ? 'DELETE' : 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      };

      if (operation.action === 'merge') {
        config.body = JSON.stringify({ products: operation.products.map((item) => item.id) });
        return [`${base}/login`, config];
      }

      config.body = JSON.stringify({});
      return [`${base}/product/${operation.id}`, config];
    }

    static async flush() {
      if (this.flushing || !FoxTheme.wishlist?.customerId || !FoxTheme.wishlist?.proxyUrl) return;
      this.flushing = true;

      while (this.getState().pending.length > 0) {
        const operation = this.getState().pending[0];
        try {
          const response = await fetch(...this.buildSyncRequest(operation));
          if (!response.ok) throw new Error(`Wishlist sync failed: ${response.status}`);
        } catch (error) {
          console.error(error);
          break; // Operation stays queued; retried on the next mutation or page load.
        }
        // Re-read after the await: clicks during the request may have queued more.
        // Only this head operation was sent, so only it is confirmed and dequeued.
        const state = this.getState();
        state.pending.shift();
        this.confirmOperation(operation, state);
        this.setState(state);
      }

      this.flushing = false;
    }

    /**
     * The metafield now reflects the operation — collapse the local overlay so
     * the in-memory picture matches what the next page load will render. The
     * effective list is unchanged, so no button refresh is needed.
     */
    static confirmOperation(operation, state) {
      const metafieldItems = FoxTheme.wishlist.metafieldItems || [];
      if (operation.action === 'merge') {
        const mergedHandles = new Set(operation.products.map((item) => item.handle));
        state.items = state.items.filter((item) => !mergedHandles.has(item.handle));
        operation.products.forEach((item) => {
          if (!metafieldItems.some((existing) => existing && existing.handle === item.handle)) {
            metafieldItems.unshift({ handle: item.handle, id: item.id });
          }
        });
      } else if (operation.action === 'add') {
        state.items = state.items.filter((item) => item.handle !== operation.handle);
        if (!metafieldItems.some((item) => item && item.handle === operation.handle)) {
          metafieldItems.unshift({ handle: operation.handle, id: operation.id });
        }
      } else {
        state.removed = state.removed.filter((handle) => handle !== operation.handle);
        FoxTheme.wishlist.metafieldItems = metafieldItems.filter(
          (item) => !item || item.handle !== operation.handle
        );
      }
    }

    static updateButtons() {
      const active = new Set(this.getItems().map((item) => item.handle));
      document.querySelectorAll('wishlist-button').forEach((button) => button.refresh(active));
    }
  }

  window.WishlistUtils = WishlistUtils;
  WishlistUtils.adoptOwner();
  WishlistUtils.flush();

  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.buttonElement = this.querySelector('button');
      this.abortController = new AbortController();
      this.buttonElement?.addEventListener('click', this.handleClick, { signal: this.abortController.signal });
      this.refresh();
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    handleClick = () => {
      WishlistUtils.toggle(this.dataset.productHandle, Number(this.dataset.productId));
    };

    refresh(activeHandles = null) {
      const active = activeHandles
        ? activeHandles.has(this.dataset.productHandle)
        : WishlistUtils.has(this.dataset.productHandle);
      this.classList.toggle('is-active', active);
      this.buttonElement?.setAttribute('aria-pressed', active ? 'true' : 'false');

      const strings = FoxTheme.wishlist?.strings || {};
      const label = active ? strings.remove : strings.add;
      if (!label) return;
      this.buttonElement?.setAttribute('aria-label', label);
    }
  }

  customElements.define('wishlist-button', WishlistButton);

  /**
   * Counter bubble on the header link. The server can only render the
   * metafield count, which ignores guest items and this device's pending
   * changes — so the element recomputes from the effective list as soon as it
   * upgrades and on every later mutation.
   */
  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this.unsubscribers = ['wishlist:add', 'wishlist:remove'].map((event) =>
        FoxTheme.pubsub.subscribe(event, this.refresh)
      );
      this.refresh();
    }

    disconnectedCallback() {
      this.unsubscribers?.forEach((unsubscribe) => unsubscribe());
    }

    refresh = () => {
      const count = WishlistUtils.getItems().length;
      // The bubble sits inside the link, so its text is part of the link's
      // accessible name ("Merkzettel 3") — no separate label needed.
      this.textContent = count > 99 ? '99+' : count;
      this.classList.toggle('cart-count--small-medium', count > 99);
      this.hidden = count === 0;
    };
  }

  customElements.define('wishlist-count', WishlistCount);
}
